import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { api } from 'services/api';
import { BudgetPayments, NewBudgetPayment } from 'types/budgetPayment';
import { PaymentMethod } from 'types/paymentMethod';

// Pagamentos, histórico e formas de pagamento de um orçamento; qualquer alteração recarrega tudo da API.
export function useBudgetPayments(budgetId: number, onChanged: () => void) {
  const [data, setData] = useState<BudgetPayments | null>(null);
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    return api
      .get<BudgetPayments>(`/budgets/${budgetId}/payments`)
      .then(response => setData(response.data))
      .catch(() => toast.error('Não foi possível carregar os pagamentos'))
      .finally(() => setLoading(false));
  }, [budgetId]);

  useEffect(() => {
    load();

    api
      .get<PaymentMethod[]>('/payment-methods/all')
      .then(response => setMethods(response.data.filter(method => method.active !== false)))
      .catch(() => toast.error('Não foi possível carregar as formas de pagamento'));
  }, [load]);

  // devolve true quando a API aceitou, para o formulário saber se pode limpar os campos
  function request(call: () => Promise<unknown>, success: string, failure: string): Promise<boolean> {
    setSaving(true);

    return call()
      .then(() => {
        toast.success(success);
        onChanged();
        return load().then(() => true);
      })
      .catch(err => {
        toast.error(err.response?.data?.error || err.response?.data?.message || failure);
        return false;
      })
      .finally(() => setSaving(false));
  }

  const addPayment = (payment: NewBudgetPayment) =>
    request(
      () => api.post(`/budgets/${budgetId}/payments`, payment),
      'Pagamento informado',
      'Não foi possível informar o pagamento',
    );

  const reversePayment = (paymentId: number, reason: string) =>
    request(
      () => api.put(`/budgets/${budgetId}/payments/${paymentId}/reverse`, { reason }),
      'Pagamento estornado',
      'Não foi possível estornar o pagamento',
    );

  return { data, methods, loading, saving, addPayment, reversePayment, reload: load };
}
